import type { ActiveFilter, ParsedQueryState } from './parser';
import type { QuerySchema } from './schema';

/** Formats a single value for query syntax, quoting if it contains spaces. */
export function formatQueryValue(val: string): string {
  const trimmed = val.trim();
  if (trimmed.includes(' ') && !trimmed.startsWith('"') && !trimmed.endsWith('"')) {
    return `"${trimmed}"`;
  }
  return trimmed;
}

/** Formats a single filter object into query syntax string. */
export function serializeFilter(filter: ActiveFilter): string {
  const { key, operator, value, values, highValue, raw } = filter;

  if (raw) {
    return raw;
  }

  // Handle multi-value OR group e.g. (compound:dry OR compound:wet)
  if (values && values.length > 1) {
    const formattedItems = values.map((v) => `${key}:${formatQueryValue(v)}`);
    return `(${formattedItems.join(' OR ')})`;
  }

  const effectiveVal = (values && values.length === 1) ? values[0] : value;
  if (!effectiveVal && effectiveVal !== '0') {
    return '';
  }

  // Date filters: "from" and "to" are already directional
  if (key === 'from') {
    if (highValue !== undefined && highValue !== '') {
      return `from:${formatQueryValue(effectiveVal)} to:${formatQueryValue(highValue)}`;
    }
    return `from:${formatQueryValue(effectiveVal)}`;
  }
  if (key === 'to') {
    return `to:${formatQueryValue(effectiveVal)}`;
  }

  switch (operator) {
    case 'between':
      if (highValue !== undefined && highValue !== '') {
        return `${key}:${effectiveVal}..${highValue}`;
      }
      return `${key}:${effectiveVal}`;
    case '>=':
      return `${key}:>=${effectiveVal}`;
    case '<=':
      return `${key}:<=${effectiveVal}`;
    case '>':
      return `${key}:>${effectiveVal}`;
    case '<':
      return `${key}:<${effectiveVal}`;
    case '=':
    default:
      return `${key}:${formatQueryValue(effectiveVal)}`;
  }
}

/**
 * Serializes parsed state (bare text + filters) into a clean, canonical search query string.
 */
export function serializeQuery(state: ParsedQueryState, _schema?: QuerySchema<string>): string {
  const parts: string[] = [];

  if (state.bareText && state.bareText.trim()) {
    parts.push(state.bareText.trim());
  }

  for (const filter of state.filters) {
    const serialized = serializeFilter(filter);
    if (serialized) {
      parts.push(serialized);
    }
  }

  if (state.rawRemainder && state.rawRemainder.trim()) {
    parts.push(state.rawRemainder.trim());
  }

  return parts.join(' ').trim();
}
